import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getClinicalData, saveClinicalForm } from '../../../../../../api/transitions';
import { getProgramsList } from '../../../../../../redux/slices/transitions';
import { trimData } from '../../../../../../utils/formatText';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import SkeletonBlock from '../../../SkeletonBlock';
import { CLINICAL } from '../../../formQuestionsEnum';
import ClinicalVendorsForm from './ClinicalVendorsForm';
import FileUploadsForm from './FileUploadsForm';
import AssociatedOrganizations from './AssociatedOrganizations';
import EquipmentForm from './EquipmentForm';
import MedicalDirectorForm from './MedicalDirectorForm';
import PhysicianServicesForm from './PhysicianServicesForm';
import FacilityProgramsForm from './FacilityProgramsForm';
import StaffInformation from './StaffInformation';

const ClinicalView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);
  const { watch } = useFormContext();
  const companyId = watch('companyId');

  const clinicalPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.clinicalFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getClinicalData(id, facilityId)
      .then((resp) => {
        if (!resp.data.success) {
          throw new Error(resp.data.message);
        }
        setData(resp.data.data);
      })
      .catch((error) => {
        enqueueSnackbar(error.message || 'Failed to load Clinical data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(CLINICAL).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await saveClinicalForm({ id, sellerFacilityId: facilityId, companyId: data?.companyId, ...result })
      .then((resp) => {
        if (!resp.data.success) {
          throw new Error(resp.data.message);
        }

        setData(resp.data.data);
        dispatch(getProgramsList(companyId));
        enqueueSnackbar('Clinical data saved successfully', { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(error.message || 'Failed to save Clinical data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <ClinicalVendorsForm permissions={clinicalPermissions} />
      <FileUploadsForm permissions={clinicalPermissions} />
      <AssociatedOrganizations permissions={clinicalPermissions} />
      <EquipmentForm permissions={clinicalPermissions} />
      <MedicalDirectorForm permissions={clinicalPermissions} />
      <PhysicianServicesForm permissions={clinicalPermissions} />
      <FacilityProgramsForm permissions={clinicalPermissions} />
      <StaffInformation permissions={clinicalPermissions} />
    </>
  );
});

export default ClinicalView;

ClinicalView.propTypes = {
  setFormData: PropTypes.func,
};
